import React, { useEffect, useState } from "react";
import { Form, Card, Alert, Button } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import SearchableSelect from "@/components/SearchableSelect";
import { BrandButton, RolesSelector } from "@/components";
import PowerBIEmbedComponent from "@/components/PowerBIEmbedComponent";
import { useAuthenticatedApi } from "@/utils/callApi";
import FileUpload from "@/components/FileUpload";
import Toast from "@/components/Toast";
import useToast from "@/components/useToast";
import typo from "@/styles/Typography.module.css";

export default function ReportCreator() {
  const { secureHelpers, isLoading, error } = useAuthenticatedApi();
  const { toast, showToast, hideToast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();

  // Check if coming from ReportMonitor
  const fromMonitor = location.state?.fromMonitor || false;
  const reportIdFromMonitor = location.state?.reportId || null;

  // Report metadata form
  const [reportTitle, setReportTitle] = useState("");
  const [reportDescription, setReportDescription] = useState("");
  const [adminNotes, setAdminNotes] = useState("");
  const [reportUri, setReportUri] = useState("");
  const [reportRoles, setReportRoles] = useState([]);
  const [currentReportDbId, setCurrentReportDbId] = useState(null);
  const [reportSaved, setReportSaved] = useState(false);
  const [reportIsLive, setReportIsLive] = useState(false);

  // Report selection for editing
  const [allReports, setAllReports] = useState([]);
  const [selectedReportId, setSelectedReportId] = useState(null);

  // Preview state
  const [users, setUsers] = useState([]);
  const [embedToken, setEmbedToken] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [UserId, setUserId] = useState(null);
  const [ReportData, setReportData] = useState(null);
  const [EmbedReportId, setEmbedReportId] = useState(null);
  const [EmbedUrl, setEmbedUrl] = useState(null);
  const [PreviewLoading, setPreviewLoading] = useState(false);
  const [PreviewError, setPreviewError] = useState(null);
  const [definitionFileId, setDefinitionFileId] = useState(null);
  const pbixFileExists = Boolean(definitionFileId);

  // Load users and hospices on mount
  useEffect(() => {
    loadUsers();
    loadReports();
  }, []);

  // Load specific report if ID passed from ReportMonitor
  useEffect(() => {
    if (reportIdFromMonitor && allReports.length > 0) {
      setSelectedReportId(reportIdFromMonitor);
      loadReportForEditing(reportIdFromMonitor);
    }
  }, [reportIdFromMonitor, allReports]);

  const loadUsers = async () => {
    try {
      const usersData = await secureHelpers.get("/api/Members");
      setUsers(usersData);
    } catch (err) {
      console.error("Failed to load users:", err);
    }
  };

  const loadReports = async () => {
    try {
      const reportsData = await secureHelpers.get("/api/Reports");
      setAllReports(reportsData);
      console.log("Loaded reports:", reportsData);
    } catch (err) {
      console.error("Failed to load reports:", err);
      showToast({
        title: "Error",
        message: "Failed to load reports list",
        variant: "error",
      });
    }
  };

  const loadReportForEditing = async (reportDbId) => {
    console.log(`✳️ Loading report for editing - ID: ${reportDbId}`);
    if (!reportDbId) {
      // Clear form for new report
      setReportTitle("");
      setReportDescription("");
      setAdminNotes("");
      setReportUri("");
      setReportRoles([]);
      setCurrentReportDbId(null);
      setReportIsLive(false);
      setReportSaved(false);
      setShowPreview(false);
      setDefinitionFileId(null);
      return;
    }

    try {
      const reportData = await secureHelpers.get(`/api/Reports/${reportDbId}`);
      console.log("Report data loaded:", reportData);
      console.log("Roles from backend:", reportData.roles || reportData.Roles);
      console.log("Full report object keys:", Object.keys(reportData));

      setReportTitle(reportData.title || reportData.Title || "");
      setReportDescription(
        reportData.description || reportData.Description || "",
      );
      setAdminNotes(reportData.adminNotes || reportData.AdminNotes || "");
      setReportUri(reportData.uri || reportData.Uri || "");
      setReportRoles(reportData.roles || reportData.Roles || []);
      setCurrentReportDbId(reportDbId);
      setReportIsLive(reportData.isLive || reportData.IsLive || false);
      setReportSaved(true);
      setShowPreview(false);

      setDefinitionFileId(
        reportData.definitionFileId &&
        reportData.definitionFileId !== "null"
          ? reportData.definitionFileId
          : null
      );
    } catch (err) {
      console.error("Failed to load report:", err);
      showToast({
        title: "Error",
        message: `Failed to load report: ${err.message}`,
        variant: "error",
      });
    }
  };

  // Create/Save report
  const saveReport = async () => {
    if (!reportTitle.trim()) {
      showToast({
        title: "Title Required",
        message: "Please enter a report title",
        variant: "warning",
      });
      return;
    }

    if (!currentReportDbId && !reportUri.trim()) {
      showToast({
        title: "URI Required",
        message: "Please enter the Power BI report URI",
        variant: "warning",
      });
      return;
    }

    try {
      const payload = {
        title: reportTitle.trim(),
        description: reportDescription.trim() || null,
        adminNotes: adminNotes.trim() || null,
        roles: reportRoles,
        isLive: reportIsLive,
      };

      console.log("Saving report with roles:", reportRoles);
      console.log("Full payload being sent:", payload);

      let result;
      if (currentReportDbId) {
        payload.id = currentReportDbId;
        payload.definitionFileId = definitionFileId;

        // Update existing
        result = await secureHelpers.put(
          `/api/Reports/${currentReportDbId}`,
          payload,
        );
        await loadReports(); // Refresh the reports list
        showToast({
          title: "Success",
          message: "Report updated successfully",
          variant: "success",
        });
      } else {
        payload.uri = reportUri.trim();

        // Create new
        result = await secureHelpers.post("/api/Reports", payload);
        setCurrentReportDbId(result.Id || result.id); // Handle PascalCase or camelCase
        await loadReports(); // Refresh the reports list
        showToast({
          title: "Success",
          message: "Report created successfully. You can now preview it below.",
          variant: "success",
        });
      }

      setReportSaved(true);
    } catch (err) {
      console.error("Failed to save report:", err);
      showToast({
        title: "Error",
        message: `Failed to save report: ${err.message}`,
        variant: "error",
      });
    }
  };

  // Download existing PBIX file

  const handleDownloadPbix = async () => {
    // check if current report exists and if so if it has a pbix file associated with it)
    if (!currentReportDbId) {
      showToast({
        title: "No Report Selected",
        message:
          "Please select or create a report first before downloading the PBIX file.",
        variant: "warning",
      });
      return;
    }
    if (!pbixFileExists) {
      showToast({
        title: "PBIX File Not Found",
        message:
          "The selected report does not have a PBIX file associated with it.",
        variant: "warning",
      });
      return;
    }

    try {
      const fileId = definitionFileId || `Report-${currentReportDbId}`;
      // get the blob from the backend
      const fileData = await secureHelpers.get(
        `/api/files/${fileId}`,
        { responseType: 'blob' },
      );
      const blob = fileData instanceof Blob
        ? fileData
        : new Blob([fileData], { type: "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = `Report-${currentReportDbId}.pbix`;

      document.body.appendChild(link);
      link.click();
      link.remove();

      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) {
      console.error("Failed to download PBIX:", error);
    }
  };

  // Clear  preview when user selection changes
  useEffect(() => {
    // Clear the embedded report when user changes
    setEmbedUrl(null);
    setEmbedToken(null);
    setEmbedReportId(null);
    setReportData(null);
  }, [UserId]);

  // Load  preview (for ing existing reports from backend)
  const loadPreview = async () => {
    if (!selectedReportId) {
      showToast({
        title: "Report Required",
        message: "Please select a report to preview",
        variant: "warning",
      });
      return;
    }

    if (!UserId) {
      showToast({
        title: "Select User",
        message: "Please select a user to preview as",
        variant: "warning",
      });
      return;
    }

    setPreviewError(null);
    try {
      setPreviewLoading(true);
      const reportId = selectedReportId;

      // Fetch report details
      console.log(`Fetching report: /api/Reports/${reportId}`);
      const reportData = await secureHelpers.get(`/api/Reports/${reportId}`);
      console.log("Report data:", reportData);
      setReportData(reportData);

      // Fetch embed token
      console.log(
        `Fetching token: /api/Reports/${reportId}/GetEmbedDetails/${UserId}`,
      );
      const tokenData = await secureHelpers.get(
        `/api/Reports/${reportId}/GetEmbedDetails/${UserId}`,
      );
      console.log("Token data:", tokenData);
      setEmbedToken(tokenData.EmbedToken || tokenData.embedToken);
      setEmbedReportId(tokenData.EmbedReportId || tokenData.embedReportId);
      setEmbedUrl(tokenData.EmbedUrl || tokenData.embedUrl);

      setShowPreview(true);

      showToast({
        title: "Success",
        message: "Report loaded successfully!",
        variant: "success",
      });
    } catch (err) {
      setPreviewLoading(false);
      console.error("Failed to load  preview:", err);

      // Clear any existing preview
      setEmbedUrl(null);
      setEmbedToken(null);
      setEmbedReportId(null);
      setShowPreview(false);

      // Determine error message based on error type
      let errorMessage = "Failed to load preview. ";

      // Check for 503 Service Unavailable (Power BI service down)
      if (
        err.message?.includes("503") ||
        err.status === 503 ||
        err.response?.status === 503
      ) {
        errorMessage =
          "Power BI service is currently unavailable. Please try again later.";
        // Check for 403 Forbidden (permission denied)
      } else if (
        err.message?.includes("403") ||
        err.status === 403 ||
        err.response?.status === 403
      ) {
        errorMessage =
          "This user does not have permission to view this report. Please select a user with the required roles or assign the necessary permissions.";
      } else if (
        err.message?.includes("404") ||
        err.status === 404 ||
        err.response?.status === 404
      ) {
        errorMessage =
          "Report not found. It may have been deleted or is no longer available.";
      } else if (err.message) {
        errorMessage += err.message;
      } else {
        errorMessage += "An unexpected error occurred. Please try again.";
      }

      setPreviewError(errorMessage);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handlePbixUploadSuccess = async () => {
    if (definitionFileId) return;

    const marker = `Report-${currentReportDbId}`;

    await secureHelpers.put(`/api/Reports/${currentReportDbId}`, {
      id: currentReportDbId,
      title: reportTitle.trim(),
      description: reportDescription.trim() || null,
      adminNotes: adminNotes.trim() || null,
      roles: reportRoles,
      isLive: reportIsLive,
      definitionFileId: marker,
    });

    setDefinitionFileId(marker);
    await loadReports();
  };

  // Publish report (toggle isLive to true)
  const publishReport = async () => {
    if (!currentReportDbId) {
      showToast({
        title: "Save First",
        message: "Please save the report before publishing",
        variant: "warning",
      });
      return;
    }

    if (reportRoles.length === 0) {
      showToast({
        title: "No Roles Assigned",
        message: "Please assign at least one role before publishing",
        variant: "warning",
      });
      return;
    }

    try {
      // Toggle isLive to true
      const payload = {
        id: currentReportDbId,
        title: reportTitle.trim(),
        description: reportDescription.trim() || null,
        adminNotes: adminNotes.trim() || null,
        roles: reportRoles,
        isLive: true, // Set to live when publishing
        definitionFileId,
      };

      console.log("Publishing report - setting isLive to true:", payload);

      await secureHelpers.put(`/api/Reports/${currentReportDbId}`, payload);
      setReportIsLive(true);
      await loadReports();

      showToast({
        title: "Success",
        message: `Report is now live and available to users with assigned roles`,
        variant: "success",
      });
    } catch (err) {
      console.error("Failed to publish report:", err);
      showToast({
        title: "Error",
        message: `Failed to publish report: ${err.message}`,
        variant: "error",
      });
    }
  };

  // Reset form
  const resetForm = () => {
    setReportTitle("");
    setReportDescription("");
    setAdminNotes("");
    setReportUri("");
    setReportRoles([]);
    setCurrentReportDbId(null);
    setReportIsLive(false);
    setReportSaved(false);
    setShowPreview(false);
    setEmbedToken(null);
    setSelectedReportId(null);
    setDefinitionFileId(null);
  };

  return (
    <div className="container py-4">
      <Toast {...toast} onClose={hideToast} />

      {fromMonitor && (
        <div className="mb-3">
          <Button
            variant="outline-secondary"
            onClick={() => navigate("/admin/reports/reportMonitor")}
            style={{
              borderColor: "#F16D09",
              color: "#F16D09",
            }}
          >
            ← Back to Manage Reports
          </Button>
        </div>
      )}

      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 style={{ color: "#F16D09" }}>Report Creator/Editor</h1>
      </div>

      {/* Report Configuration Form */}
      <Card className="mb-4">
        <Card.Header style={{ backgroundColor: "#F16D09", color: "white" }}>
          <h5 className="mb-0">Report Configuration</h5>
        </Card.Header>
        <Card.Body>
          {/* Report Selector for Editing */}
          <div
            className="mb-4 p-3"
            style={{ backgroundColor: "#f8f9fa", borderRadius: "4px" }}
          >
            <Form.Group>
              <Form.Label style={{ color: "#2e1719" }}>
                <span
                  style={{ fontFamily: "'Neutraface-Demi', Arial, sans-serif" }}
                >
                  Select a report to edit
                </span>
              </Form.Label>
              <SearchableSelect
                options={allReports}
                value={selectedReportId || ""}
                onChange={(reportId) => {
                  console.log("Selected report ID:", reportId);
                  setSelectedReportId(reportId);
                  loadReportForEditing(reportId);
                }}
                placeholder="Choose an existing report or leave blank for new..."
                getLabel={(report) =>
                  report.title || report.Title || "Untitled Report"
                }
                getValue={(report) => {
                  const id = report.id || report.Id;
                  console.log("Report getValue:", report, "ID:", id);
                  return id;
                }}
              />
            </Form.Group>
          </div>

          <p className="text-muted mb-3" style={{ fontSize: "0.9rem" }}>
            <small>* indicates required field</small>
          </p>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Report Title *</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter report title"
                value={reportTitle}
                onChange={(e) => setReportTitle(e.target.value)}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description (visible to users)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Description that users will see"
                value={reportDescription}
                onChange={(e) => setReportDescription(e.target.value)}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Admin Notes (internal only)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="Internal notes for admins"
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Power BI Report URI *</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="https://app.powerbi.com/..."
                value={reportUri}
                onChange={(e) => setReportUri(e.target.value)}
                style={{ fontFamily: "monospace", fontSize: "0.9rem" }}
              />
              <Form.Text className="text-muted">
                Power BI embed URLs can be over 100 characters long
              </Form.Text>
            </Form.Group>

            {/* Roles Selector */}
            <div className="mb-3">
              <label className="form-label">Assign to Roles</label>
              <RolesSelector
                selectedRoles={reportRoles}
                onRolesChange={(newRoles) => {
                  console.log("Roles changed to:", newRoles);
                  setReportRoles(newRoles);
                }}
                title="Report Roles"
              />
              {reportRoles.length > 0 && (
                <div
                  className="mt-2 p-2"
                  style={{ backgroundColor: "#e7f3ff", borderRadius: "4px" }}
                >
                  <small>
                    <span
                      style={{
                        fontFamily: "'Neutraface-Demi', Arial, sans-serif",
                      }}
                    >
                      Currently assigned to:
                    </span>{" "}
                    {reportRoles.join(", ")}
                  </small>
                </div>
              )}
            </div>

            {/* Live Status Indicator */}
            <div className="mb-3">
              <div
                className="p-3"
                style={{
                  backgroundColor: reportIsLive ? "#d4edda" : "#fff3cd",
                  borderRadius: "4px",
                  border: `1px solid ${reportIsLive ? "#28a745" : "#ffc107"}`,
                }}
              >
                <span
                  style={{ fontFamily: "'Neutraface-Demi', Arial, sans-serif" }}
                >
                  Report Status:{" "}
                </span>
                <span
                  style={{
                    color: reportIsLive ? "#28a745" : "#856404",
                  }}
                >
                  {reportIsLive
                    ? "✓ LIVE - Visible to users"
                    : "⚠ DRAFT - Not visible to users"}
                </span>
                {!reportIsLive && (
                  <div className="mt-2">
                    <small>
                      Click "Make Available to Users" to publish this report
                    </small>
                  </div>
                )}
              </div>
            </div>

            <div className="d-flex gap-2">
              <BrandButton onClick={saveReport}>
                {currentReportDbId ? "Update Report Details" : "Create Report"}
              </BrandButton>

              {reportSaved && (
                <BrandButton onClick={publishReport}>
                  Make Available to Users
                </BrandButton>
              )}

              <div style={{ width: "2rem" }}></div>

              <BrandButton
                onClick={() => navigate("/admin/reports/reportMonitor")}
                variant="outline"
              >
                Back to Monitoring
              </BrandButton>

              <BrandButton onClick={resetForm} variant="outline">
                Clear Form
              </BrandButton>
            </div>
          </Form>
        </Card.Body>
      </Card>

      {/* Power BI Template File Upload  */}

      <Card className="mb-4">
        <Card.Header style={{ backgroundColor: "#F16D09", color: "white" }}>
          <h5 className="mb-0">Power BI Template File (.pbix)</h5>
        </Card.Header>
        {/* center content in card */}
        <Card
          style={{
            flex: 1,
            margin: "1rem",
            backgroundColor: "var(--brand-light)",
            padding: "0.5rem",
            borderRadius: "4px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <h4
            style={{
              fontFamily: "'Neutraface-Book', Arial, sans-serif",
              marginBottom: ".5rem",
            }}
          >
            Existing Power BI Template File for Report Titled:{" "}
            {reportTitle}{" "}
          </h4>
          <p className="text-muted mb-1" style={{ fontSize: typo["font-lg"] }}>
            {pbixFileExists
              ? `A .pbix file for the report titled "${reportTitle}" is currently uploaded and associated with this report. You can download the existing file, or upload a new one to replace it.`
              : "No .pbix file has been uploaded for this report yet."}
          </p>

          {pbixFileExists && (
            <BrandButton
              onClick={() => {
                handleDownloadPbix();
              }}
              style={{
                width: "fit-content",
                padding: ".5rem",
                marginBottom: "2rem",
                justifyContent: "center",
                alignItems: "center",
                fontSize: typo["font-base"],
              }}
            >{`Download Power BI Template for Current File`}</BrandButton>
          )}
        </Card>
        <Card.Body>
          {!reportTitle && (
            <div>
              <p
                className="text-muted mb-1"
                style={{ fontSize: typo["font-lg"] }}
              >
                Optionally upload the .pbix file for this report so others can
                use it as a template or extract different metrics).
              </p>
              <p
                className="text-muted mb-3"
                style={{ fontSize: typo["font-lg"] }}
              >
                The file will be stored against this report, title:{" "}
                {reportTitle}. You can replace it at any time by uploading
                again.
              </p>
            </div>
          )}

          {pbixFileExists && (
            <Alert variant="warning">
              You already have a pbix file uploaded for this report (see panel
              above). Uploading a new file will replace the existing one. Make
              sure to download the current file if you want to keep it before
              uploading a new one.
            </Alert>
          )}
          {currentReportDbId && (
            <FileUpload
              endpoint={`/api/files/Report-${currentReportDbId}`}
              onUploadSuccess={handlePbixUploadSuccess}
              fileTypeLabel=".pbix"
              accept=".pbix"
            />
          )}
          {!currentReportDbId && (
            <Alert variant="warning">
              Please save the report first to enable file upload. The upload
              area will appear here once the report is saved and the upload can
              be linked to it successfully.
            </Alert>
          )}
        </Card.Body>
      </Card>

      {/*  Preview Section (for ing existing backend reports) */}
      <Card className="mb-4">
        <Card.Header style={{ backgroundColor: "#6c757d", color: "white" }}>
          <h5 className="mb-0"> Report Preview</h5>
        </Card.Header>
        <Card.Body>
          <Alert variant="info" className="mb-3">
            <span
              style={{ fontFamily: "'Neutraface-Demi', Arial, sans-serif" }}
            >
              Previewing Mode:
            </span>{" "}
            Load an existing report from the backend to the preview
            functionality
          </Alert>

          {PreviewError && (
            <Alert
              variant="danger"
              className="mb-3"
              onClose={() => setPreviewError(null)}
              dismissible
            >
              <Alert.Heading>Unable to Load Report</Alert.Heading>
              {PreviewError}
            </Alert>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Select Report</Form.Label>
            <SearchableSelect
              options={allReports}
              value={selectedReportId}
              getLabel={(report) =>
                `${report.title || report.Title} (ID: ${report.id || report.Id})`
              }
              getValue={(report) => report.id || report.Id}
              onChange={(reportId) => {
                console.log("Selected report ID:", reportId);
                setSelectedReportId(reportId);
              }}
              placeholder="Search for a report..."
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Select User to Preview As</Form.Label>
            <SearchableSelect
              options={users}
              value={UserId}
              getLabel={(user) =>
                `${user.firstName} ${user.lastName} (${user.email})`
              }
              getValue={(user) => user.id}
              onChange={(userId) => {
                console.log("Selected user ID:", userId);
                setUserId(userId);
              }}
              placeholder="Search for a user..."
            />
          </Form.Group>

          <BrandButton
            onClick={loadPreview}
            className="mb-3"
            disabled={PreviewLoading || !selectedReportId || !UserId}
          >
            {PreviewLoading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Loading Preview...
              </>
            ) : (
              "Load  Preview"
            )}
          </BrandButton>

          {PreviewLoading && (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2 text-muted">
                Loading report preview, please wait...
              </p>
            </div>
          )}

          {showPreview &&
            ReportData &&
            EmbedUrl &&
            embedToken &&
            EmbedReportId && (
              <div
                style={{
                  border: "2px solid #ddd",
                  borderRadius: "8px",
                  padding: "20px",
                  marginTop: "20px",
                  backgroundColor: "#f8f9fa",
                  width: "100%",
                }}
              >
                <Alert variant="success" className="mb-3">
                  <span
                    style={{
                      fontFamily: "'Neutraface-Demi', Arial, sans-serif",
                    }}
                  >
                    REPORT LOADED:
                  </span>{" "}
                  {ReportData.Title || ReportData.title}
                  <br />
                  <span
                    style={{
                      fontFamily: "'Neutraface-Demi', Arial, sans-serif",
                    }}
                  >
                    DESCRIPTION:
                  </span>{" "}
                  {ReportData.Description || ReportData.description}
                  <br />
                  <span
                    style={{
                      fontFamily: "'Neutraface-Demi', Arial, sans-serif",
                    }}
                  >
                    REPORT URI:
                  </span>{" "}
                  {ReportData.Uri || ReportData.uri}
                  <br />
                  <span
                    style={{
                      fontFamily: "'Neutraface-Demi', Arial, sans-serif",
                    }}
                  >
                    EMBED URL:
                  </span>{" "}
                  {EmbedUrl}
                  <br />
                  <span
                    style={{
                      fontFamily: "'Neutraface-Demi', Arial, sans-serif",
                    }}
                  >
                    EMBED REPORT ID:
                  </span>{" "}
                  {EmbedReportId}
                </Alert>

                <div style={{ width: "100%", height: "800px" }}>
                  <PowerBIEmbedComponent
                    embedUrl={EmbedUrl}
                    accessToken={embedToken}
                    reportId={EmbedReportId}
                    pageName="Implicit"
                  />
                </div>
              </div>
            )}
        </Card.Body>
      </Card>
    </div>
  );
}
